// openRunsForDelivery — the runs an unallocated delivery can go to.
//
// Same match as Runs' "Add another delivery": the run is still open on the
// packing side (PACKING_OPEN_STATUSES, never a hand-typed list), is for the
// same variety, and is for the same grower. A run stores grower_id when it has
// one; an older run without it matches on the grower NAME instead.
// Read-only: it never links anything.
import { PACKING_OPEN_STATUSES } from '@/lib/runStatus'
import { withReadTimeout } from '@/lib/saveTimeout'

export interface OpenRunRow {
  run_id: string
  run_number: string
  status: string
  grower: string
  grower_id: string | null
  variety: string
  started_at: string | null
}

export interface DeliveryForRuns {
  grower: string
  grower_id: string | null
  variety: string
}

const norm = (s: string | null | undefined) => (s ?? '').trim().toLowerCase()

/** Pure: which of these runs can take this delivery. Newest first. */
export function matchOpenRuns(delivery: DeliveryForRuns, runs: readonly OpenRunRow[]): OpenRunRow[] {
  const open: readonly string[] = PACKING_OPEN_STATUSES
  return runs
    .filter(r => open.includes(r.status))
    .filter(r => norm(r.variety) === norm(delivery.variety))
    .filter(r => r.grower_id && delivery.grower_id
      ? r.grower_id === delivery.grower_id
      : norm(r.grower) === norm(delivery.grower))
    .sort((a, b) => (b.started_at ?? '').localeCompare(a.started_at ?? ''))
}

type Db = { from: (table: string) => any }

/** The read. THROWS on any error so the caller can say "could not read",
 *  never "no runs". Soft-deleted runs are never offered. Narrowed by variety on the server; grower matched here. */
export async function openRunsForDelivery(db: Db, delivery: DeliveryForRuns): Promise<OpenRunRow[]> {
  const { data, error } = await withReadTimeout(
    db.from('runs')
      .select('run_id, run_number, status, grower, grower_id, variety, started_at')
      .in('status', [...PACKING_OPEN_STATUSES])
      .is('deleted_at', null)
      .eq('variety', delivery.variety)
      .order('started_at', { ascending: false })
      .limit(200) as PromiseLike<{ data: OpenRunRow[] | null; error: { message: string } | null }>,
    'open runs for delivery',
  )
  if (error) throw new Error(error.message)
  return matchOpenRuns(delivery, data ?? [])
}
